import React, { useCallback, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, useQuery, useMutation } from '@tanstack/react-query';
import { DiaryDayPage } from '@traquility/ui/diary';
import type { DiaryCategory, DiaryDay, DiaryField, DiaryFieldSet } from '@traquility/ui/diary';

// Create a query client for this component tree
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

interface DiaryDayPageContainerProps {
  /** The diary date (YYYY-MM-DD) */
  diaryDate: string;
  /** GraphQL endpoint URL */
  graphqlEndpoint: string;
  /** CSRF token for API requests */
  csrfToken: string;
  /** Icon base URL */
  iconBaseUrl?: string;
}

// GraphQL Queries
const GET_DIARY_DAY_QUERY = `
  query GetDiaryDayByDate($date: String!) {
    diaryDay(date: $date) {
      id
      date
      mood
      energy
      note
      activities {
        id
        active
        value
        fieldId
        numericFieldSetId
        automaticallyTriggered
      }
    }
  }
`;

const GET_CATEGORIES_QUERY = `
  query GetDiaryCategories {
    diaryCategories {
      id
      name
      position
      fields {
        id
        name
        icon
        score
        type
        fieldSetId
        position
        hasGoal
        minValue
        maxValue
        stepValue
        units
      }
      fieldSets {
        id
        name
        position
        categoryId
        hasGoal
        fields {
          id
          name
          icon
          score
          type
          fieldSetId
          position
          hasGoal
        }
      }
      numericFieldSets {
        id
        name
        icon
        position
        categoryId
        units
        minValue
        maxValue
        labels {
          id
          name
          color
          value
          position
        }
      }
    }
    categoriesManager {
      scoreWidgets {
        id
        name
        position
        editUrl
        deleteUrl
        scoreFields {
          id
          name
          icon
          minValue
          maxValue
          stepValue
          units
          score
        }
      }
    }
  }
`;

// GraphQL Mutations
const TRIGGER_FIELD_ACTIVITY = `
  mutation TriggerFieldActivity($input: TriggerFieldActivityInput!) {
    triggerFieldActivity(input: $input) {
      activity {
        id
        active
        value
        fieldId
        automaticallyTriggered
      }
      diaryDay {
        id
        activities {
          id
          active
          value
          fieldId
          automaticallyTriggered
        }
      }
    }
  }
`;

const TRIGGER_FIELD_SET_ACTIVITY = `
  mutation TriggerFieldSetActivity($input: TriggerFieldSetActivityInput!) {
    triggerFieldSetActivity(input: $input) {
      activities {
        id
        active
        value
        fieldId
        automaticallyTriggered
      }
      diaryDay {
        id
        activities {
          id
          active
          value
          fieldId
          automaticallyTriggered
        }
      }
    }
  }
`;

const TRIGGER_NUMERIC_ACTIVITY = `
  mutation TriggerNumericActivity($input: TriggerNumericActivityInput!) {
    triggerNumericActivity(input: $input) {
      activity {
        id
        active
        value
        fieldId
        automaticallyTriggered
      }
    }
  }
`;

const TRIGGER_NUMERIC_FIELD_SET_ACTIVITY = `
  mutation TriggerNumericFieldSetActivity($diaryDate: String!, $numericFieldSetId: ID!, $value: Int, $labelId: ID) {
    triggerNumericFieldSetActivity(diaryDate: $diaryDate, numericFieldSetId: $numericFieldSetId, value: $value, labelId: $labelId) {
      activity {
        id
        active
        value
        numericFieldSetId
        automaticallyTriggered
      }
    }
  }
`;

const UPDATE_DIARY_DAY = `
  mutation UpdateDiaryDay($diaryDate: String!, $mood: Int, $energy: Int, $note: String) {
    updateDiaryDay(diaryDate: $diaryDate, mood: $mood, energy: $energy, note: $note) {
      diaryDay {
        id
        date
        mood
        energy
        note
      }
    }
  }
`;

const TRIGGER_SCORE_WIDGET_FIELD_ACTIVITY = `
  mutation TriggerScoreWidgetFieldActivity($diaryDate: String!, $fieldId: ID!, $value: Int!) {
    triggerNumericActivity(input: { diaryDate: $diaryDate, fieldId: $fieldId, value: $value, triggerAction: "set_value" }) {
      activity {
        id
        active
        value
        fieldId
      }
    }
  }
`;

function DiaryDayPageInner({
  diaryDate,
  graphqlEndpoint,
  csrfToken,
  iconBaseUrl = '/assets',
}: DiaryDayPageContainerProps) {
  const [optimisticActivities, setOptimisticActivities] = useState<Record<string, any>>({});

  // Fetch diary day data (uses initialData if provided to skip network request)
  const { data: diaryDayData, refetch: refetchDiaryDay } = useQuery({
    queryKey: ['diaryDay', diaryDate],
    queryFn: async () => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: GET_DIARY_DAY_QUERY,
          variables: { date: diaryDate },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch diary day');
      }

      const result = await response.json();
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'GraphQL error');
      }

      return result.data.diaryDay;
    },
  });

  // Fetch categories (uses initialData if provided to skip network request)
  const { data: categoriesData } = useQuery({
    queryKey: ['diaryCategories'],
    queryFn: async () => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: GET_CATEGORIES_QUERY,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }

      const result = await response.json();
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'GraphQL error');
      }

      return {
        categories: result.data.diaryCategories,
        scoreWidgets: result.data.categoriesManager?.scoreWidgets || [],
      };
    },
  });

  // Mutation for toggle/field set activities
  const fieldActivityMutation = useMutation({
    mutationFn: async ({ fieldId }: { fieldId: string }) => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: TRIGGER_FIELD_ACTIVITY,
          variables: {
            input: {
              diaryDate,
              fieldId,
            },
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to trigger field activity');
      }

      const result = await response.json();
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'GraphQL error');
      }

      return result.data.triggerFieldActivity;
    },
  });

  // Mutation for field set activities
  const fieldSetActivityMutation = useMutation({
    mutationFn: async ({ fieldSetId, fieldId }: { fieldSetId: string; fieldId?: string }) => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: TRIGGER_FIELD_SET_ACTIVITY,
          variables: {
            input: {
              diaryDate,
              fieldSetId,
              fieldId,
            },
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to trigger field set activity');
      }

      const result = await response.json();
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'GraphQL error');
      }

      return result.data.triggerFieldSetActivity;
    },
  });

  // Mutation for numeric activities
  const numericActivityMutation = useMutation({
    mutationFn: async ({ fieldId, value, action }: { fieldId: string; value: number; action: string }) => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: TRIGGER_NUMERIC_ACTIVITY,
          variables: {
            input: {
              diaryDate,
              fieldId,
              value,
              triggerAction: action,
            },
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to trigger numeric activity');
      }

      const result = await response.json();
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'GraphQL error');
      }

      return result.data.triggerNumericActivity;
    },
  });

  // Mutation for numeric field set activities
  const numericFieldSetActivityMutation = useMutation({
    mutationFn: async ({ numericFieldSetId, value, labelId }: { numericFieldSetId: string; value?: number; labelId?: string }) => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: TRIGGER_NUMERIC_FIELD_SET_ACTIVITY,
          variables: {
            diaryDate,
            numericFieldSetId,
            value,
            labelId,
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to trigger numeric field set activity');
      }

      const result = await response.json();
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'GraphQL error');
      }

      return result.data.triggerNumericFieldSetActivity;
    },
  });

  // Mutation for score widget field activities
  const scoreWidgetFieldMutation = useMutation({
    mutationFn: async ({ fieldId, value }: { fieldId: string; value: number }) => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: TRIGGER_SCORE_WIDGET_FIELD_ACTIVITY,
          variables: {
            diaryDate,
            fieldId,
            value,
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to trigger score widget field activity');
      }

      const result = await response.json();
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'GraphQL error');
      }

      return result.data.triggerNumericActivity;
    },
  });

  // Mutation for updating diary day (note, mood, energy)
  const updateDiaryDayMutation = useMutation({
    mutationFn: async ({ mood, energy, note }: { mood?: number; energy?: number; note?: string }) => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: UPDATE_DIARY_DAY,
          variables: {
            diaryDate,
            mood,
            energy,
            note,
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update diary day');
      }

      const result = await response.json();
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'GraphQL error');
      }

      return result.data.updateDiaryDay;
    },
  });

  const handleFieldClick = useCallback((fieldId: string) => {
    // Optimistic update: toggle the field immediately
    const currentActivity = diaryDayData.activities.find((a: any) => a.fieldId === fieldId);
    setOptimisticActivities(prev => ({
      ...prev,
      [fieldId]: {
        id: currentActivity?.id || `temp-${fieldId}`,
        fieldId,
        active: !currentActivity?.active,
        value: currentActivity?.value || null,
        automaticallyTriggered: false,
      },
    }));

    fieldActivityMutation.mutate({ fieldId }, {
      onSuccess: (data) => {
        // Update cache with mutation response, then clear optimistic state
        queryClient.setQueryData(['diaryDay', diaryDate], data.diaryDay);

        // Clear optimistic state after cache is updated
        setOptimisticActivities(prev => {
          const newState = { ...prev };
          delete newState[fieldId];
          return newState;
        });
      },
      onError: () => {
        // Revert optimistic state on error
        setOptimisticActivities(prev => {
          const newState = { ...prev };
          delete newState[fieldId];
          return newState;
        });
      },
    });
  }, [fieldActivityMutation, diaryDayData, diaryDate, queryClient]);

  const handleFieldSetClick = useCallback((fieldSetId: string, fieldId?: string) => {
    fieldSetActivityMutation.mutate({ fieldSetId, fieldId }, {
      onSuccess: (data) => {
        // Update cache with mutation response
        queryClient.setQueryData(['diaryDay', diaryDate], data.diaryDay);
        setOptimisticActivities({});
      },
      onError: () => {
        setOptimisticActivities({});
      },
    });
  }, [fieldSetActivityMutation, diaryDate, queryClient]);

  const handleNumericFieldChange = useCallback((fieldId: string, value: number, action: 'set_value' | 'plus' | 'minus') => {
    // Optimistic update for numeric fields
    const currentActivity = diaryDayData.activities.find((a: any) => a.fieldId === fieldId);
    setOptimisticActivities(prev => ({
      ...prev,
      [fieldId]: {
        id: currentActivity?.id || `temp-${fieldId}`,
        fieldId,
        active: true,
        value: value,
        automaticallyTriggered: false,
      },
    }));

    numericActivityMutation.mutate({ fieldId, value, action }, {
      onSuccess: (data) => {
        // Update the specific activity in cache
        queryClient.setQueryData(['diaryDay', diaryDate], (oldData: any) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            activities: oldData.activities.map((a: any) =>
              a.fieldId === fieldId ? data.activity : a
            ),
          };
        });

        // Clear optimistic state after cache is updated
        setOptimisticActivities(prev => {
          const newState = { ...prev };
          delete newState[fieldId];
          return newState;
        });
      },
      onError: () => {
        // Revert optimistic state on error
        setOptimisticActivities(prev => {
          const newState = { ...prev };
          delete newState[fieldId];
          return newState;
        });
      },
    });
  }, [numericActivityMutation, diaryDayData, diaryDate, queryClient]);

  const handleNumericFieldSetClick = useCallback((numericFieldSetId: string) => {
    // Find the numeric field set to get its labels
    const numericFieldSet = categoriesData
      ?.flatMap((cat: any) => cat.numericFieldSets || [])
      .find((nfs: any) => nfs.id === numericFieldSetId);

    if (!numericFieldSet) return;

    // Find current activity
    const currentActivity = diaryDayData.activities.find((a: any) => a.numericFieldSetId === numericFieldSetId);
    const currentValue = currentActivity?.value;

    // Find next label to cycle to
    const sortedLabels = [...numericFieldSet.labels].sort((a: any, b: any) => a.position - b.position);
    const currentLabelIndex = sortedLabels.findIndex((l: any) => l.value === currentValue);
    const nextLabel = sortedLabels[(currentLabelIndex + 1) % sortedLabels.length];

    numericFieldSetActivityMutation.mutate(
      { numericFieldSetId, value: nextLabel.value, labelId: nextLabel.id },
      {
        onSuccess: (data) => {
          // Update cache with new activity
          queryClient.setQueryData(['diaryDay', diaryDate], (oldData: any) => {
            if (!oldData) return oldData;

            const existingActivity = oldData.activities.find((a: any) => a.numericFieldSetId === numericFieldSetId);
            if (existingActivity) {
              return {
                ...oldData,
                activities: oldData.activities.map((a: any) =>
                  a.numericFieldSetId === numericFieldSetId ? data.activity : a
                ),
              };
            } else {
              return {
                ...oldData,
                activities: [...oldData.activities, data.activity],
              };
            }
          });
        },
      }
    );
  }, [numericFieldSetActivityMutation, categoriesData, diaryDayData, diaryDate, queryClient]);

  const handleNumericFieldSetValueChange = useCallback((numericFieldSetId: string, value: number, labelId?: string) => {
    numericFieldSetActivityMutation.mutate(
      { numericFieldSetId, value, labelId },
      {
        onSuccess: (data) => {
          // Update cache with new activity
          queryClient.setQueryData(['diaryDay', diaryDate], (oldData: any) => {
            if (!oldData) return oldData;

            const existingActivity = oldData.activities.find((a: any) => a.numericFieldSetId === numericFieldSetId);
            if (existingActivity) {
              return {
                ...oldData,
                activities: oldData.activities.map((a: any) =>
                  a.numericFieldSetId === numericFieldSetId ? data.activity : a
                ),
              };
            } else {
              return {
                ...oldData,
                activities: [...oldData.activities, data.activity],
              };
            }
          });
        },
      }
    );
  }, [numericFieldSetActivityMutation, diaryDate, queryClient]);

  const handleScoreWidgetFieldChange = useCallback((fieldId: string, value: number) => {
    // Optimistic update for score widget fields
    const currentActivity = diaryDayData.activities.find((a: any) => a.fieldId === fieldId);
    setOptimisticActivities(prev => ({
      ...prev,
      [fieldId]: {
        id: currentActivity?.id || `temp-${fieldId}`,
        fieldId,
        active: true,
        value: value,
        automaticallyTriggered: false,
      },
    }));

    scoreWidgetFieldMutation.mutate({ fieldId, value }, {
      onSuccess: (data) => {
        // Update the specific activity in cache
        queryClient.setQueryData(['diaryDay', diaryDate], (oldData: any) => {
          if (!oldData) return oldData;

          const existingActivity = oldData.activities.find((a: any) => a.fieldId === fieldId);
          if (existingActivity) {
            return {
              ...oldData,
              activities: oldData.activities.map((a: any) =>
                a.fieldId === fieldId ? data.activity : a
              ),
            };
          } else {
            return {
              ...oldData,
              activities: [...oldData.activities, data.activity],
            };
          }
        });

        // Clear optimistic state after cache is updated
        setOptimisticActivities(prev => {
          const newState = { ...prev };
          delete newState[fieldId];
          return newState;
        });
      },
      onError: () => {
        // Revert optimistic state on error
        setOptimisticActivities(prev => {
          const newState = { ...prev };
          delete newState[fieldId];
          return newState;
        });
      },
    });
  }, [scoreWidgetFieldMutation, diaryDayData, diaryDate, queryClient]);

  const handleNoteChange = useCallback((note: string) => {
    // Optimistic update
    queryClient.setQueryData(['diaryDay', diaryDate], (oldData: any) => {
      if (!oldData) return oldData;
      return { ...oldData, note };
    });

    updateDiaryDayMutation.mutate(
      { note },
      {
        onSuccess: (data) => {
          queryClient.setQueryData(['diaryDay', diaryDate], (oldData: any) => {
            if (!oldData) return oldData;
            return { ...oldData, ...data.diaryDay };
          });
        },
        onError: () => {
          // Revert optimistic update
          refetchDiaryDay();
        },
      }
    );
  }, [updateDiaryDayMutation, diaryDate, queryClient, refetchDiaryDay]);

  if (!diaryDayData || !categoriesData?.categories) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Merge optimistic updates with real data
  const mergedDiaryDay = {
    ...diaryDayData,
    activities: diaryDayData.activities.map((activity: any) => {
      const optimistic = optimisticActivities[activity.fieldId];
      return optimistic || activity;
    }).concat(
      // Add new optimistic activities that don't exist in real data yet
      Object.values(optimisticActivities).filter(
        (opt: any) => !diaryDayData.activities.some((a: any) => a.fieldId === opt.fieldId)
      )
    ),
  };

  return (
    <DiaryDayPage
      date={diaryDate}
      diaryDay={mergedDiaryDay}
      categories={categoriesData.categories}
      scoreWidgets={categoriesData.scoreWidgets || []}
      onFieldClick={handleFieldClick}
      onFieldSetClick={handleFieldSetClick}
      onNumericFieldChange={handleNumericFieldChange}
      onNumericFieldSetClick={handleNumericFieldSetClick}
      onNumericFieldSetValueChange={handleNumericFieldSetValueChange}
      onScoreWidgetFieldChange={handleScoreWidgetFieldChange}
      onNoteChange={handleNoteChange}
      isLoading={fieldActivityMutation.isPending || fieldSetActivityMutation.isPending || numericActivityMutation.isPending || numericFieldSetActivityMutation.isPending || scoreWidgetFieldMutation.isPending || updateDiaryDayMutation.isPending}
      iconBaseUrl={iconBaseUrl}
    />
  );
}

/**
 * DiaryDayPageContainer - Entry point for rendering diary day in Rails
 *
 * This component wraps the shared DiaryDayPage with:
 * - React Query provider
 * - GraphQL query/mutation handling
 * - CSRF token handling for Rails
 */
export function DiaryDayPageContainer(props: DiaryDayPageContainerProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <DiaryDayPageInner {...props} />
    </QueryClientProvider>
  );
}

/**
 * Mount function for rendering from Rails
 */
export function mountDiaryDayPage(
  container: HTMLElement,
  props: DiaryDayPageContainerProps
) {
  const root = createRoot(container);
  root.render(<DiaryDayPageContainer {...props} />);
  return root;
}

export default DiaryDayPageContainer;
